"use client";

/**
 * Who a message typed on a task's screen is for (spec 2026-09-06 §3.7, D2):
 * the selection IS the recipient. The chip on the bench, the screen in the
 * column and the box read ONE key (teamViewStore), so the three can never
 * disagree. Moved out of MessageInput verbatim so the Dock's pill, which is
 * the composer on the task screen with one-composer-everywhere on, addresses
 * a lane by the same rule and not a copy of it.
 */
import { useCallback } from "react";
import { useLaneState, type LaneState } from "@/stores/selectors";
import { useLaneInView } from "@/stores/teamViewStore";

/**
 * R5: strip ONE leading `@handle ` the person typed for the lane already in
 * view, so the prefix never doubles. Case-insensitive, because the routing
 * parse (`parseTeammateMention`) is.
 *
 * The whitespace is the whole rule: `@mangalyaan-costCredits per kg?` is a
 * SENTENCE that opens with the handle's letters, not a mention of it.
 */
export function stripLeadingHandle(text: string, handle: string): string {
  if (!text.toLowerCase().startsWith(`@${handle.toLowerCase()}`)) return text;
  const rest = text.slice(handle.length + 1);
  return /^\s/.test(rest) ? rest.trimStart() : text;
}

export interface LaneRecipient {
  /** The lane whose screen has the column, or null for Bash. */
  laneInView: string | null;
  /** The lane in view was never given an inbox (D8): it cannot be sent to. */
  laneHasNoInbox: boolean;
  /** The lane a send is addressed to, or null for Bash. */
  laneRecipient: LaneState | null;
  /** R5 lives in ONE place: with a lane in view the words are prefixed with
   *  its roster handle exactly once; with Bash in view they are untouched. */
  addressToLane: (text: string) => string;
}

/** `createdTaskId` is the CREATED task in view, never a pending placeholder. */
export function useLaneRecipient(createdTaskId: string | null): LaneRecipient {
  const laneInView = useLaneInView(createdTaskId);
  const lane = useLaneState(createdTaskId, laneInView);
  const laneHasNoInbox = lane !== null && !lane.addressable;
  const laneRecipient = lane !== null && lane.addressable ? lane : null;
  const addressToLane = useCallback(
    (text: string) =>
      laneRecipient
        ? `@${laneRecipient.handle} ${stripLeadingHandle(
            text,
            laneRecipient.handle
          )}`
        : text,
    [laneRecipient]
  );
  return { laneInView, laneHasNoInbox, laneRecipient, addressToLane };
}
